/**
 * TanStack-Query key factory for the Forecast v0.1 surfaces (Phase D.2).
 *
 * Each panel that wants live cache reads/writes goes through one of
 * these helpers so the dashboard composition stays cache-aligned.
 *
 * Scope tuples:
 *  - All Forecast surfaces are team-scoped (cross-cycle by design),
 *    NOT cycle-scoped — the trajectory is computed across the team's
 *    last N published cycles, not against a single cycle.
 *  - Lead vs `/me` variants split (mirror Comply C.2's split).
 *  - The what-if key includes the lever id + a serialised params hash
 *    so re-projecting a different lever doesn't collide in cache;
 *    callers building the key normally just use a stable serialiser
 *    (the `extend_contract` membership_id is fine to include — it's a
 *    cache key, not an audit payload).
 *
 * The terminating `accountId` element matches the rest of the SPA's
 * query-key shape — the server scopes by Bearer token, but the SPA
 * still keys on the account so a sign-out invalidates everything for
 * the prior id.
 */

export const forecastCoverageKey = (
  teamId: string,
  horizonCycles: number,
  accountId: string | null,
) => ["forecast", "coverage_trajectory", teamId, horizonCycles, accountId] as const;

export const forecastFairnessDriftKey = (
  teamId: string,
  horizonCycles: number,
  accountId: string | null,
) => ["forecast", "fairness_drift", teamId, horizonCycles, accountId] as const;

export const forecastMyFairnessDriftKey = (
  teamId: string,
  horizonCycles: number,
  accountId: string | null,
) => ["forecast", "me", "fairness_drift", teamId, horizonCycles, accountId] as const;

export const forecastWhatIfKey = (
  teamId: string,
  lever: string,
  paramsKey: string,
  accountId: string | null,
) => ["forecast", "what_if", teamId, lever, paramsKey, accountId] as const;

// Issue #162 — hire-by projection (lead-only)
export const forecastHireProjectionKey = (teamId: string, accountId: string | null) =>
  ["forecast", "hire_projection", teamId, accountId] as const;

// Issue #162 — worker-side sustainability bar
export const meForecastSustainabilityKey = (accountId: string | null) =>
  ["forecast", "me", "sustainability", accountId] as const;
